import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { reviewAPI } from '../../services/api';
import '../../styles/admin-reviews.css';

const AdminReviews = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        entity_type: 'all'
    });

    const loadReviews = useCallback(async () => {
        try {
            setLoading(true);
            const response = await reviewAPI.getAllReviews();
            setReviews(response.data?.data || []);
        } catch (error) {
            console.error('Load reviews error:', error);
            alert(error.response?.data?.message || 'Failed to load reviews');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadReviews();
    }, [loadReviews]);

    const filteredReviews = useMemo(() => {
        const query = filters.search.trim().toLowerCase();
        return reviews.filter((review) => {
            const statusMatch = filters.status === 'all' || review.status === filters.status;
            const typeMatch = filters.entity_type === 'all' || review.entity_type === filters.entity_type;
            const searchMatch = !query || [review.customer_name, review.customer_email, review.review]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(query));
            return statusMatch && typeMatch && searchMatch;
        });
    }, [filters, reviews]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleStatus = async (review, status) => {
        try {
            setUpdating(true);
            await reviewAPI.updateReviewStatus(review.id, status);
            setReviews((prev) => prev.map((item) => item.id === review.id ? { ...item, status } : item));
        } catch (error) {
            console.error('Update review status error:', error);
            alert(error.response?.data?.message || 'Failed to update review');
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = async (review) => {
        if (!window.confirm('Delete this review?')) return;
        try {
            setUpdating(true);
            await reviewAPI.deleteReview(review.id);
            setReviews((prev) => prev.filter((item) => item.id !== review.id));
        } catch (error) {
            console.error('Delete review error:', error);
            alert(error.response?.data?.message || 'Failed to delete review');
        } finally {
            setUpdating(false);
        }
    };

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main admin-reviews-page">
                <AdminHeader title="Reviews Moderation" />
                <section className="coupon-hero">
                    <div>
                        <span className="coupon-eyebrow">Trust & Social Proof</span>
                        <h2>Approve customer reviews before they go live</h2>
                        <p>Keep package and camping reviews clean, useful, and trustworthy for future travelers.</p>
                    </div>
                </section>

                <div className="filters-card">
                    <div className="filters-header">
                        <h3><i className="fas fa-filter"></i> Filter Reviews</h3>
                    </div>
                    <div className="filters-body">
                        <div className="filters-grid">
                            <div className="filter-group">
                                <label>Search</label>
                                <input name="search" value={filters.search} onChange={handleFilterChange} placeholder="Search by name, email or text" />
                            </div>
                            <div className="filter-group">
                                <label>Status</label>
                                <select name="status" value={filters.status} onChange={handleFilterChange}>
                                    <option value="all">All</option>
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>
                            <div className="filter-group">
                                <label>Entity Type</label>
                                <select name="entity_type" value={filters.entity_type} onChange={handleFilterChange}>
                                    <option value="all">All</option>
                                    <option value="package">Package</option>
                                    <option value="camping">Camping</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="admin-content">
                    {loading ? (
                        <div className="loading-state"><div className="spinner"></div><p>Loading reviews...</p></div>
                    ) : (
                        <div className="reviews-admin-grid">
                            {filteredReviews.map((review) => (
                                <div key={review.id} className="review-admin-card">
                                    <div className="review-admin-top">
                                        <div>
                                            <strong>{review.customer_name}</strong>
                                            <p>{review.customer_email || 'Email not provided'}</p>
                                        </div>
                                        <span className={`status-badge ${review.status}`}>{review.status}</span>
                                    </div>
                                    <div className="review-admin-meta">
                                        <span>{review.entity_type} #{review.entity_id}</span>
                                        <span>{new Date(review.created_at).toLocaleDateString('en-IN')}</span>
                                        <span>{review.rating}/5</span>
                                    </div>
                                    <p className="review-admin-text">{review.review}</p>
                                    <div className="review-admin-actions">
                                        <button disabled={updating} onClick={() => handleStatus(review, 'approved')}>Approve</button>
                                        <button disabled={updating} onClick={() => handleStatus(review, 'rejected')}>Reject</button>
                                        <button disabled={updating} className="danger" onClick={() => handleDelete(review)}>Delete</button>
                                    </div>
                                </div>
                            ))}
                            {!filteredReviews.length && <div className="empty-cell">No reviews found.</div>}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminReviews;
